import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { zMetricNumericType } from '@strength/domain';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { trpc } from '../../../../trpc/react';
import { Drawer, DrawerFooter, DrawerHeader } from '../../../components/drawer';
import { Form, FormField, FormItem } from '../../../components/form/form';
import { ConfirmationModal } from '../../../components/modals/confirmation-modal';
import { useToast } from '../../../hooks/useToast';
import type { AdminMetric } from './types';
import { parseCommaSeparated } from './utils';

export const zMetricForm = z.object({
  name: z.string().min(1, 'Name is required'),
  sortOrder: z.coerce.number(),
  categoryId: z.uuid().nullable(),
  isCollectable: z.boolean(),
  numericType: zMetricNumericType,
  allowRange: z.boolean(),
  units: z
    .string()
    .refine(
      (value) => !parseCommaSeparated(value).includes('%'),
      '% is handled by Allow Percentage, not the units list',
    ),
  keywords: z.string(),
  isInverseScale: z.boolean(),
  isPercent: z.boolean(),
});

type MetricFormValues = z.infer<typeof zMetricForm>;

export function metricToFormValues(metric: AdminMetric): MetricFormValues {
  return {
    name: metric.name,
    sortOrder: metric.sortOrder,
    categoryId: metric.categoryId ?? null,
    numericType: metric.config.numericType,
    isCollectable: metric.isCollectable,
    allowRange: metric.config.allowRange,
    units: metric.config.units.join(', '),
    keywords: metric.config.keywords.join(', '),
    isInverseScale: metric.config.isInverseScale ?? false,
    isPercent: metric.config.isPercent ?? false,
  };
}

export function formValuesToConfig(data: MetricFormValues) {
  return {
    numericType: data.numericType,
    allowRange: data.allowRange,
    units: parseCommaSeparated(data.units),
    keywords: parseCommaSeparated(data.keywords),
    isInverseScale: data.isInverseScale,
    isPercent: data.isPercent,
  };
}

const defaultValues: MetricFormValues = {
  name: '',
  sortOrder: 0,
  categoryId: null,
  numericType: 'integer',
  isCollectable: true,
  allowRange: false,
  units: '',
  keywords: '',
  isInverseScale: false,
  isPercent: false,
};

export function MetricFormDrawer({
  open,
  onClose,
  orgId,
  metric,
  defaultSortOrder,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  metric?: AdminMetric;
  defaultSortOrder?: number;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const isEditMode = !!metric;

  const form = useForm<MetricFormValues>({
    mode: 'onChange',
    resolver: zodResolver(zMetricForm),
    defaultValues: metric
      ? metricToFormValues(metric)
      : { ...defaultValues, sortOrder: defaultSortOrder ?? 0 },
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = form;

  const categoriesQuery = trpc.admin.metrics.getCategoriesByOrg.useQuery({
    orgId,
  });
  const categories = categoriesQuery.data ?? [];

  const createMutation = trpc.admin.metrics.createMetric.useMutation({
    onSuccess: () => {
      toast({ title: 'Metric created', severity: 'success' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Error creating metric', severity: 'error' });
    },
  });

  const updateMutation = trpc.admin.metrics.updateMetric.useMutation({
    onSuccess: () => {
      toast({ title: 'Metric updated', severity: 'success' });
      onSuccess();
    },
    onError: () => {
      toast({ title: 'Error updating metric', severity: 'error' });
    },
  });

  const onSubmit = async (data: MetricFormValues) => {
    const config = formValuesToConfig(data);

    if (isEditMode && metric) {
      await updateMutation.mutateAsync({
        orgId,
        id: metric.id,
        name: data.name,
        type: metric.type,
        sortOrder: data.sortOrder,
        categoryId: data.categoryId,
        isCollectable: data.isCollectable,
        isDefault: metric.isDefault,
        recordType: null,
        config,
      });
    } else {
      await createMutation.mutateAsync({
        orgId,
        name: data.name,
        type: 'custom',
        sortOrder: data.sortOrder,
        categoryId: data.categoryId,
        isCollectable: data.isCollectable,
        isDefault: false,
        recordType: null,
        config,
      });
    }
  };

  const closeHandler = useCallback(() => {
    if (isDirty) {
      setCancelModalOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  return (
    <Drawer open={open} onClose={closeHandler}>
      <DrawerHeader onClose={closeHandler}>
        <Typography variant="h3">
          {isEditMode ? 'Edit Metric' : 'Create Metric'}
        </Typography>
      </DrawerHeader>

      <Form {...form}>
        <form
          id="metric-form"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col grow px-6 gap-4 overflow-y-auto"
        >
          <FormField
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  {...field}
                  label="Name"
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                />
              </FormItem>
            )}
          />

          <FormField
            name="sortOrder"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  {...field}
                  type="number"
                  label="Sort Order"
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                />
              </FormItem>
            )}
          />

          <FormField
            name="categoryId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  select
                  label="Category"
                  name={field.name}
                  value={field.value ?? ''}
                  onChange={(event) =>
                    field.onChange(
                      event.target.value === '' ? null : event.target.value,
                    )
                  }
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                >
                  <MenuItem value="">None</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </TextField>
              </FormItem>
            )}
          />

          <FormField
            name="numericType"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  {...field}
                  select
                  label="Numeric Type"
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                >
                  {zMetricNumericType.options.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </FormItem>
            )}
          />

          <FormField
            name="isCollectable"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControlLabel
                  control={
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  }
                  label="Collectable"
                />
              </FormItem>
            )}
          />

          <FormField
            name="allowRange"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControlLabel
                  control={
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  }
                  label="Allow Range"
                />
              </FormItem>
            )}
          />

          <FormField
            name="isInverseScale"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControlLabel
                  control={
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  }
                  label="Inverse Scale (lower value is better)"
                />
              </FormItem>
            )}
          />

          <FormField
            name="isPercent"
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormControlLabel
                  control={
                    <Checkbox checked={field.value} onChange={field.onChange} />
                  }
                  label="Allow Percentage"
                />
              </FormItem>
            )}
          />

          <FormField
            name="units"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  {...field}
                  label="Units (comma-separated)"
                  placeholder="kg, lbs"
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                />
              </FormItem>
            )}
          />

          <FormField
            name="keywords"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <TextField
                  {...field}
                  label="Keywords (comma-separated)"
                  placeholder="AMRAP, EMOM"
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                />
              </FormItem>
            )}
          />
        </form>
      </Form>

      <DrawerFooter className="flex flex-row gap-2 w-full p-6 justify-between">
        <Button variant="outlined" onClick={closeHandler}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="metric-form"
          variant="contained"
          disabled={!isDirty}
        >
          {isEditMode ? 'Save' : 'Create'}
        </Button>
      </DrawerFooter>

      <ConfirmationModal
        id="metric-form-discard"
        open={cancelModalOpen}
        title="Discard Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        onConfirm={() => {
          setCancelModalOpen(false);
          onClose();
        }}
        onCancel={() => setCancelModalOpen(false)}
      />
    </Drawer>
  );
}
